import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { HomepageMethod } from './methodsData';
import MethodDetailModal from './MethodDetailModal';
import './MethodsLibrarySection.css';

const highlightIcons = ['📚', '🔧', '💳'];

/* ═══════════════════════════════════════════
   WUNDERFRAGE PREVIEW
   ═══════════════════════════════════════════ */

const MethodPreviewCard: React.FC = () => {
  const { t } = useTranslation();
  const preview = t('methods.preview', { returnObjects: true }) as any;
  const meta = preview.meta as string[];
  const steps = preview.steps as string[];

  return (
    <div className="ml-preview-card">
      <div className="ml-preview-header">
        <span className="ml-preview-icon">✨</span>
        <div>
          <div className="ml-preview-title">{preview.title}</div>
          <div className="ml-preview-cat">{preview.category}</div>
        </div>
      </div>
      <div className="ml-preview-meta">
        {meta.map((m: string) => <span key={m}>{m}</span>)}
      </div>
      <p className="ml-preview-desc">{preview.description}</p>
      <div className="ml-preview-steps">
        {steps.map((step: string, i: number) => (
          <div key={i} className="ml-preview-step"><span>{i + 1}</span> {step}</div>
        ))}
        <div className="ml-preview-step-more">{preview.moreSteps}</div>
      </div>
      <Link to="/methoden" className="ml-preview-link">
        {preview.detailsLink}
      </Link>
    </div>
  );
};

/* ═══════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════ */

const MethodsLibrarySection: React.FC = () => {
  const { t } = useTranslation();
  const [selectedMethod, setSelectedMethod] = useState<HomepageMethod | null>(null);
  const headerRef = useScrollAnimation<HTMLDivElement>();
  const highlightsRef = useScrollAnimation<HTMLDivElement>(0.05);
  const previewRef = useScrollAnimation<HTMLDivElement>(0.1);

  const highlights = t('methods.highlights', { returnObjects: true }) as {
    title: string; description: string; features: string[];
  }[];
  const stats = t('methods.stats', { returnObjects: true }) as {
    number: string; label: string; sub: string;
  }[];

  return (
    <section id="methods" className="ml-section ml-section--dark" aria-labelledby="methods-title">
      <div className="ml-section-inner">

        {/* Section Header */}
        <div ref={headerRef} className="hp-section-header hp-animate">
          <span className="hp-section-label ml-label-light">{t('methods.sectionLabel')}</span>
          <h2 id="methods-title" className="ml-title-light">{t('methods.title')}</h2>
          <p className="ml-sub-light">{t('methods.subtitle')}</p>
        </div>

        {/* 3 Highlight Cards */}
        <div ref={highlightsRef} className="ml-highlights hp-animate hp-animate-stagger">
          {highlights.map((h, idx) => (
            <div key={idx} className="ml-highlight-card ml-highlight-card--dark hp-animate-child" style={{ transitionDelay: `${idx * 0.1}s` }}>
              <span className="ml-highlight-icon" aria-hidden="true">{highlightIcons[idx]}</span>
              <h3>{h.title}</h3>
              <p>{h.description}</p>
              {h.features && (
                <ul className="ml-highlight-features">
                  {h.features.map((f, fi) => (
                    <li key={fi}><span className="ml-check">✓</span> {f}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        {/* Preview Card + Stats */}
        <div ref={previewRef} className="ml-preview-section hp-animate">
          <MethodPreviewCard />

          {/* Stats Bar */}
          <div className="ml-stats-bar ml-stats-bar--dark">
            {stats.map((s, idx) => (
              <div key={idx} className="ml-stat">
                <div className="ml-stat-number">{s.number}</div>
                <div className="ml-stat-label">{s.label}</div>
                {s.sub && <div className="ml-stat-sub">{s.sub}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="ml-cta-row">
          <Link
            to="/methoden"
            className="ml-cta-btn ml-cta-btn--outline-light"
          >
            {t('methods.ctaLibrary')}
          </Link>
          <Link
            to="/methoden"
            className="ml-cta-btn ml-cta-btn--primary"
          >
            {t('methods.ctaShop')}
          </Link>
        </div>

      </div>

      {/* Method Detail Modal */}
      <MethodDetailModal method={selectedMethod} onClose={() => setSelectedMethod(null)} />
    </section>
  );
};

export default MethodsLibrarySection;
